/**
 * FL-132: デモ案件データローダー
 *
 * caseData.tsの案件データをデータベースに投入する処理
 * デモリセット機能と連携して使用
 */

import { getSupabaseClient } from '../../utils/supabase';
import { TODAY_CASES, HISTORICAL_CASES, SCHEDULED_CASES } from './caseData';
import { prepareDemoPartners } from './partnerData';
import { Case, DemoCaseLoadResult } from '../../types';
import { DEMO_COMPANY_ID } from '../../constants/demo';
import { logger } from '../../utils/logger';

/**
 * デモ案件データをデータベースに投入
 */
export async function loadDemoCasesToDatabase(): Promise<DemoCaseLoadResult> {
  const supabase = getSupabaseClient();
  if (!supabase) {
    return {
      success: false,
      message: 'Supabaseクライアントが初期化されていません',
      counts: { historical: 0, today: 0, scheduled: 0, total: 0 },
    };
  }

  try {
    logger.info('Starting to load demo cases', {
      component: 'DemoCaseLoader',
      companyId: DEMO_COMPANY_ID,
    });

    // 既存のデモ会社の案件を削除（クリーンアップ）
    const { error: deleteError } = await supabase
      .from('company_cases')
      .delete()
      .eq('company_id', DEMO_COMPANY_ID);

    if (deleteError) {
      logger.warn('Failed to delete existing cases', {
        component: 'DemoCaseLoader',
        companyId: DEMO_COMPANY_ID,
        error: deleteError,
      });
    }

    // デモ取引先データを投入（案件データより先に投入）
    await insertDemoPartners(supabase, DEMO_COMPANY_ID);

    // 案件データを準備（company_casesテーブル用にマッピング）
    const prepareCases = (cases: Partial<Case>[]): any[] => {
      return cases.map(c => ({
        company_id: DEMO_COMPANY_ID,
        // idは文字列のまま使用（Supabaseが自動的にUUIDとして扱う）
        id: c.id,
        status: c.status,
        // 取引先IDはそのまま使用（既にUUID形式）
        billing_partner_id: c.billingPartnerId,
        billing_location_id: c.billingLocationId,
        billing_address_custom: c.billingAddressCustom,
        pickup_partner_id: c.pickupPartnerId,
        pickup_location_id: c.pickupLocationId,
        pickup_address_custom: c.pickupAddressCustom || c.pickupAddress,
        pickup_phone: c.pickupPhone,
        pickup_date: c.pickupDate,
        pickup_date_end: c.pickupDateEnd,
        pickup_time_start: c.pickupTimeStart,
        pickup_time_end: c.pickupTimeEnd,
        delivery_partner_id: c.deliveryPartnerId,
        delivery_location_id: c.deliveryLocationId,
        delivery_address_custom: c.deliveryAddressCustom || c.deliveryAddress,
        delivery_phone: c.deliveryPhone,
        delivery_date: c.deliveryDate,
        delivery_date_end: c.deliveryDateEnd,
        delivery_time: c.deliveryTime,
        vehicle_plate_number: c.vehiclePlateNumber,
        vehicle_model: c.vehicleModel, // company_casesではvehicle_modelフィールド
        assigned_transporter_id: c.assignedLandTransporterId,
        transport_fee: c.transportFee,
        requester: c.requester,
        management_number: c.managementNumber,
        remarks: c.remarks,
        is_vehicle_inspection_expired: c.isVehicleInspectionExpired,
        requires_temporary_number: c.requiresTemporaryNumber,
        temporary_number_notes: c.temporaryNumberNotes,
      }));
    };

    // 過去案件を投入
    const historicalData = prepareCases(HISTORICAL_CASES);
    let historicalCount = 0;
    if (historicalData.length > 0) {
      const { data: historicalResult, error: historicalError } = await supabase
        .from('company_cases')
        .insert(historicalData)
        .select();

      if (historicalError) {
        logger.error('Failed to insert historical cases', {
          component: 'DemoCaseLoader',
          error: historicalError,
          dataCount: historicalData.length,
        });
      } else {
        historicalCount = historicalResult?.length || 0;
        logger.info('Inserted historical cases', {
          component: 'DemoCaseLoader',
          count: historicalCount,
        });
      }
    }

    // 当日案件を投入
    const todayData = prepareCases(TODAY_CASES);
    let todayCount = 0;
    if (todayData.length > 0) {
      const { data: todayResult, error: todayError } = await supabase
        .from('company_cases')
        .insert(todayData)
        .select();

      if (todayError) {
        logger.error('Failed to insert today cases', {
          component: 'DemoCaseLoader',
          error: todayError,
          dataCount: todayData.length,
        });
      } else {
        todayCount = todayResult?.length || 0;
        logger.info('Inserted today cases', {
          component: 'DemoCaseLoader',
          count: todayCount,
        });
      }
    }

    // 未来案件を投入
    const scheduledData = prepareCases(SCHEDULED_CASES);
    let scheduledCount = 0;
    if (scheduledData.length > 0) {
      const { data: scheduledResult, error: scheduledError } = await supabase
        .from('company_cases')
        .insert(scheduledData)
        .select();

      if (scheduledError) {
        logger.error('Failed to insert scheduled cases', {
          component: 'DemoCaseLoader',
          error: scheduledError,
          dataCount: scheduledData.length,
        });
      } else {
        scheduledCount = scheduledResult?.length || 0;
        logger.info('Inserted scheduled cases', {
          component: 'DemoCaseLoader',
          count: scheduledCount,
        });
      }
    }

    const totalCount = historicalCount + todayCount + scheduledCount;

    logger.info('Successfully loaded demo cases', {
      component: 'DemoCaseLoader',
      totalCount,
      breakdown: {
        historical: historicalCount,
        today: todayCount,
        scheduled: scheduledCount,
      },
    });

    return {
      success: true,
      message: `デモ案件データを${totalCount}件投入しました`,
      counts: {
        historical: historicalCount,
        today: todayCount,
        scheduled: scheduledCount,
        total: totalCount,
      },
    };
  } catch (error) {
    logger.error('Error loading demo cases', {
      component: 'DemoCaseLoader',
      error: error instanceof Error ? error.message : 'Unknown error',
      companyId: DEMO_COMPANY_ID,
    });
    return {
      success: false,
      message: `デモ案件データの投入に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
      counts: { historical: 0, today: 0, scheduled: 0, total: 0 },
    };
  }
}

/**
 * 特定の日付の案件データのみを更新
 */
export async function updateCasesForDate(
  date: string,
  cases: Partial<Case>[]
): Promise<{
  success: boolean;
  message: string;
  count: number;
}> {
  const supabase = getSupabaseClient();
  if (!supabase) {
    return {
      success: false,
      message: 'Supabaseクライアントが初期化されていません',
      count: 0,
    };
  }

  try {
    // 指定日付の既存案件を削除
    const { error: deleteError } = await supabase
      .from('company_cases')
      .delete()
      .eq('company_id', DEMO_COMPANY_ID)
      .eq('pickup_date', date);

    if (deleteError) {
      logger.warn('Failed to delete cases for date', {
        component: 'DemoCaseLoader',
        date,
        error: deleteError,
      });
    }

    // 新しい案件データを投入
    if (cases.length > 0) {
      const caseData = cases.map(c => ({
        ...c,
        company_id: DEMO_COMPANY_ID,
        billing_partner_id: c.billingPartnerId,
        pickup_partner_id: c.pickupPartnerId,
        delivery_partner_id: c.deliveryPartnerId,
      }));

      const { data: insertResult, error: insertError } = await supabase
        .from('company_cases')
        .insert(caseData)
        .select();

      if (insertError) {
        throw insertError;
      }

      const count = insertResult?.length || 0;
      return {
        success: true,
        message: `${date}の案件を${count}件更新しました`,
        count,
      };
    }

    return {
      success: true,
      message: `${date}の案件データはありません`,
      count: 0,
    };
  } catch (error) {
    logger.error('Error updating cases for date', {
      component: 'DemoCaseLoader',
      date,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    return {
      success: false,
      message: `案件データの更新に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
      count: 0,
    };
  }
}

/**
 * デモ取引先データを投入
 */
async function insertDemoPartners(supabase: any, companyId: string): Promise<void> {
  try {
    logger.info('Starting to insert demo partners', {
      component: 'DemoCaseLoader',
      companyId,
    });

    // 既存のカスタム取引先データを削除（デモ用固定IDのもののみ）
    const demoPartnerIds = [
      '11111111-1111-1111-1111-111111111111',
      '22222222-2222-2222-2222-222222222222',
      '33333333-3333-3333-3333-333333333333',
      '44444444-4444-4444-4444-444444444444',
      '55555555-5555-5555-5555-555555555555',
      '66666666-6666-6666-6666-666666666666',
    ];

    // 関連するlocation データを先に削除
    const { error: locationsDeleteError } = await supabase
      .from('business_partner_locations')
      .delete()
      .in('business_partner_id', demoPartnerIds);

    if (locationsDeleteError) {
      logger.warn('Failed to delete existing demo partner locations', {
        component: 'DemoCaseLoader',
        error: locationsDeleteError,
      });
    }

    // デモ取引先データを削除
    const { error: partnersDeleteError } = await supabase
      .from('business_partners')
      .delete()
      .in('id', demoPartnerIds)
      .eq('company_id', companyId);

    if (partnersDeleteError) {
      logger.warn('Failed to delete existing demo partners', {
        component: 'DemoCaseLoader',
        error: partnersDeleteError,
      });
    }

    // 新しいデモ取引先データを投入
    const demoPartners = prepareDemoPartners(companyId);
    const partnersForInsert = demoPartners.map(partner => ({
      ...partner,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }));

    const { error: insertError } = await supabase
      .from('business_partners')
      .insert(partnersForInsert);

    if (insertError) {
      throw new Error(`Failed to insert demo partners: ${insertError.message}`);
    }

    logger.info('Demo partners inserted successfully', {
      component: 'DemoCaseLoader',
      count: partnersForInsert.length,
    });
  } catch (error) {
    logger.error('Error inserting demo partners', {
      component: 'DemoCaseLoader',
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    throw error;
  }
}

export default {
  loadDemoCasesToDatabase,
  updateCasesForDate,
};
